/**
 * Token Refresh Service
 * Automatically refreshes Okta tokens before they expire
 */

import logger from '../utils/logger';

class TokenRefreshService {
  constructor() {
    this.refreshInterval = null;
    this.isRefreshing = false;
  }

  /**
   * Start automatic token refresh
   * @param {Object} oktaAuth - Okta Auth instance
   */
  startAutoRefresh(oktaAuth) {
    logger.info('[TOKEN-REFRESH] Starting automatic token refresh service');

    // Clear any existing interval
    this.stopAutoRefresh();

    // Check and refresh tokens every 5 minutes
    this.refreshInterval = setInterval(async () => {
      await this.checkAndRefreshTokens(oktaAuth);
    }, 5 * 60 * 1000); // 5 minutes

    // Also check immediately
    this.checkAndRefreshTokens(oktaAuth);
  }

  /**
   * Stop automatic token refresh
   */
  stopAutoRefresh() {
    if (this.refreshInterval) {
      logger.info('[TOKEN-REFRESH] Stopping automatic token refresh service');
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Check if tokens need refresh and refresh them
   * @param {Object} oktaAuth - Okta Auth instance
   */
  async checkAndRefreshTokens(oktaAuth) {
    if (this.isRefreshing) {
      logger.debug('[TOKEN-REFRESH] Already refreshing, skipping...');
      return;
    }

    try {
      this.isRefreshing = true;

      const isAuthenticated = await oktaAuth.isAuthenticated();
      if (!isAuthenticated) {
        logger.debug('[TOKEN-REFRESH] Not authenticated, skipping refresh');
        return;
      }

      const idToken = await oktaAuth.tokenManager.get('idToken');
      const accessToken = await oktaAuth.tokenManager.get('accessToken');

      if (!idToken || !accessToken) {
        logger.debug('[TOKEN-REFRESH] No tokens found, skipping refresh');
        return;
      }

      // Check if tokens are about to expire (within 10 minutes)
      const now = Math.floor(Date.now() / 1000);
      const idTokenExpiry = idToken.expiresAt;
      const accessTokenExpiry = accessToken.expiresAt;

      const idTokenExpiresIn = idTokenExpiry - now;
      const accessTokenExpiresIn = accessTokenExpiry - now;

      logger.debug('[TOKEN-REFRESH] Token expiry check:', {
        idTokenExpiresIn: `${Math.floor(idTokenExpiresIn / 60)} minutes`,
        accessTokenExpiresIn: `${Math.floor(accessTokenExpiresIn / 60)} minutes`
      });

      // Refresh if tokens expire in less than 10 minutes
      const shouldRefresh = idTokenExpiresIn < 600 || accessTokenExpiresIn < 600;

      if (shouldRefresh) {
        logger.info('[TOKEN-REFRESH] Tokens expiring soon, refreshing...');

        try {
          await oktaAuth.tokenManager.renew('idToken');
          logger.info('[TOKEN-REFRESH] ID Token refreshed successfully');

          await oktaAuth.tokenManager.renew('accessToken');
          logger.info('[TOKEN-REFRESH] Access Token refreshed successfully');

        } catch (renewError) {
          logger.error('[TOKEN-REFRESH] Failed to renew tokens:', renewError);

          // If refresh fails, try to get new tokens silently
          try {
            logger.info('[TOKEN-REFRESH] Attempting silent authentication...');
            await oktaAuth.token.getWithoutPrompt({
              responseType: ['id_token', 'token'],
              scopes: ['openid', 'profile', 'email']
            });
            logger.info('[TOKEN-REFRESH] Silent authentication successful');
          } catch (silentError) {
            logger.error('[TOKEN-REFRESH] Silent authentication failed:', silentError);
            logger.warn('[TOKEN-REFRESH] User may need to log in again');
          }
        }
      } else {
        logger.debug('[TOKEN-REFRESH] Tokens are still valid, no refresh needed');
      }

    } catch (error) {
      logger.error('[TOKEN-REFRESH] Error checking tokens:', error);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Force refresh tokens immediately
   * @param {Object} oktaAuth - Okta Auth instance
   */
  async forceRefresh(oktaAuth) {
    logger.info('[TOKEN-REFRESH] Force refreshing tokens...');
    await this.checkAndRefreshTokens(oktaAuth);
  }
}

// Export singleton instance
const tokenRefreshService = new TokenRefreshService();
export default tokenRefreshService;
